import { useMemo, useState } from "react";
import {
  Users,
  UserPlus,
  RefreshCw,
  ShieldCheck,
  UserCheck,
  UserX,
  Eye,
  Trash2,
  Crown,
  Search,
} from "lucide-react";
import styles from "./UsersOverview.module.css";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import AddUserModal from "../../../components/UsersPermissions/AddUserModal";
import UserDetail from "../UserDetail/UserDetail";
import { useToast } from "../../../context/ToastContext";
import {
  useUsersList,
  useUserStats,
  useDeleteUser,
} from "../../../api/users/useUsersApi";
import { useRolesList } from "../../../api/users/useRolesApi";

const roleBadgeClass = (name = "") => {
  const n = name.toLowerCase();
  if (n.includes("super")) return styles.roleSuper;
  if (n === "admin") return styles.roleAdmin;
  if (n === "customer") return styles.roleCustomer;
  return styles.roleUser;
};

const statusBadgeClass = (status = "") => {
  const s = status.toUpperCase();
  if (s === "ACTIVE") return styles.stActive;
  if (s === "SUSPENDED") return styles.stSuspended;
  return styles.stDeactivated;
};

const prettyRole = (name = "") =>
  name
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "—";

const UsersOverview = () => {
  const { showToast } = useToast();

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [role, setRole] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  const { data: users, pagination, loading, error, refetch } = useUsersList({
    page,
    limit,
    search,
    status,
    role,
    sortBy: "createdAt",
    sortOrder: "DESC",
  });
  const { data: stats, refetch: refetchStats } = useUserStats();
  const { data: roles } = useRolesList();
  const { execute: deleteUser, loading: deleting } = useDeleteUser();

  const roleOptions = useMemo(
    () => [
      { label: "All Roles", value: "" },
      ...(Array.isArray(roles)
        ? roles
            .filter((r) => r.status === "ACTIVE")
            .map((r) => ({ label: prettyRole(r.name), value: r.name }))
        : []),
    ],
    [roles]
  );

  const adminCount =
    (stats?.roleDistribution?.superAdmin ?? 0) + (stats?.adminUsers ?? 0);

  const tiles = [
    {
      key: "total",
      label: "Total Users",
      value: stats?.totalUsers ?? "—",
      icon: <Users size={20} />,
      tone: "blue",
      hint: "All backend accounts",
    },
    {
      key: "active",
      label: "Active",
      value: stats?.activeUsers ?? "—",
      icon: <UserCheck size={20} />,
      tone: "green",
      hint: "Currently enabled",
    },
    {
      key: "admins",
      label: "Administrators",
      value: stats ? adminCount : "—",
      icon: <ShieldCheck size={20} />,
      tone: "orange",
      hint: "Super Admin + Admin",
    },
    {
      key: "inactive",
      label: "Inactive",
      value: stats?.inactiveUsers ?? "—",
      icon: <UserX size={20} />,
      tone: "red",
      hint: "Suspended / deactivated",
    },
  ];

  const pageNumbers = useMemo(() => {
    const total = pagination?.totalPages ?? 1;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = new Set([1, total]);
    for (let i = Math.max(1, page - 2); i <= Math.min(total, page + 2); i++) {
      pages.add(i);
    }
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (p > prev + 1) result.push("…");
      result.push(p);
      prev = p;
    }
    return result;
  }, [pagination?.totalPages, page]);

  const handleSearch = (e) => {
    e?.preventDefault?.();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleRefresh = async () => {
    const res = await refetch();
    refetchStats();
    if (res?.success === false) showToast(res.message, "error");
    else showToast("User list refreshed", "success");
  };

  const handleCreated = () => {
    setShowAdd(false);
    refetch();
    refetchStats();
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteUser(userToDelete.id);
      showToast("User deleted successfully", "success");
      setUserToDelete(null);
      refetch();
      refetchStats();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete user", "error");
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <Users size={22} className={styles.titleIcon} />
            Users &amp; Permissions
          </h1>
          <p className={styles.subtitle}>
            Manage backend users, roles and access control
          </p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.refreshBtn} onClick={handleRefresh}>
            <RefreshCw size={16} />
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <button className={styles.addBtn} onClick={() => setShowAdd(true)}>
            <UserPlus size={16} />
            Add User
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className={styles.statGrid}>
        {tiles.map((t) => (
          <div key={t.key} className={`${styles.statCard} ${styles[t.tone]}`}>
            <div className={styles.statIcon}>{t.icon}</div>
            <div className={styles.statBody}>
              <span className={styles.statValue}>{t.value}</span>
              <span className={styles.statLabel}>{t.label}</span>
              <span className={styles.statHint}>{t.hint}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <Search size={16} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by name or email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.searchBtn}>
            Search
          </button>
        </form>

        <SelectBox
          placeholder="All Status"
          value={status}
          width="180px"
          options={[
            { label: "All Status", value: "" },
            { label: "Active", value: "ACTIVE" },
            { label: "Suspended", value: "SUSPENDED" },
            { label: "Deactivated", value: "DEACTIVATED" },
          ]}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        />

        <SelectBox
          placeholder="All Roles"
          value={role}
          width="190px"
          options={roleOptions}
          onChange={(e) => {
            setRole(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Status</th>
              <th>Created</th>
              <th>Last Updated</th>
              <th className={styles.right}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className={styles.center}>
                  Loading users…
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                      <Users size={30} />
                    </div>
                    <p className={styles.emptyTitle}>No users found</p>
                    <span className={styles.emptyHint}>
                      Try adjusting your filters, or add a new user to get started.
                    </span>
                  </div>
                </td>
              </tr>
            ) : (
              users.map((u) => {
                const roleName =
                  u.currentRoleName || u.roles?.[0]?.name || u.roleNames || "—";
                return (
                  <tr
                    key={u.id}
                    className={styles.row}
                    onClick={() => setDetailId(u.id)}
                  >
                    <td>
                      <div className={styles.userCell}>
                        <img
                          src={`https://i.pravatar.cc/64?u=${u.email}`}
                          alt={u.name}
                          className={styles.avatar}
                        />
                        <div>
                          <div className={styles.userName}>
                            {u.name}
                            {roleName.toLowerCase().includes("super") && (
                              <Crown size={13} className={styles.crown} />
                            )}
                          </div>
                          <div className={styles.userEmail}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`${styles.roleBadge} ${roleBadgeClass(roleName)}`}>
                        {prettyRole(roleName)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${statusBadgeClass(u.status)}`}
                      >
                        {prettyRole(u.status)}
                      </span>
                    </td>
                    <td className={styles.muted}>{fmtDate(u.createdAt)}</td>
                    <td className={styles.muted}>{fmtDate(u.updatedAt)}</td>
                    <td className={styles.right} onClick={(e) => e.stopPropagation()}>
                      <div className={styles.actions}>
                        <button
                          className={styles.iconBtn}
                          title="View / edit"
                          onClick={() => setDetailId(u.id)}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className={`${styles.iconBtn} ${styles.danger}`}
                          title="Delete user"
                          onClick={() => setUserToDelete(u)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Footer / pagination */}
        <div className={styles.footer}>
          <span className={styles.footerInfo}>
            Showing {users.length} of {pagination?.totalUsers ?? 0} users
          </span>
          {(pagination?.totalPages ?? 1) > 1 && (
            <div className={styles.pageNumbers}>
              <button
                className={styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹
              </button>
              {pageNumbers.map((p, i) =>
                p === "…" ? (
                  <span key={`e${i}`} className={styles.ellipsis}>
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    className={`${styles.pageBtn} ${
                      Number(p) === Number(page) ? styles.pageBtnActive : ""
                    }`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                className={styles.pageBtn}
                disabled={page >= (pagination?.totalPages ?? 1)}
                onClick={() =>
                  setPage((p) => Math.min(pagination?.totalPages ?? 1, p + 1))
                }
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals / drawer */}
      <AddUserModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        roles={roles}
        onCreated={handleCreated}
      />

      {detailId && (
        <UserDetail
          userId={detailId}
          onClose={() => setDetailId(null)}
          onSaved={() => {
            refetch();
            refetchStats();
          }}
        />
      )}

      <DeleteConfirmModal
        open={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        title="Delete user"
        itemName={userToDelete?.name}
        message={
          userToDelete
            ? `Are you sure you want to delete "${userToDelete.name}" (${userToDelete.email})? This action cannot be undone.`
            : undefined
        }
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />
    </div>
  );
};

export default UsersOverview;
